import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import ServiceCard from './components/ServiceCard';



import SafetyBadge from './components/SafetyBadge';

import ServiceTabs from './components/ServiceTabs';

const ServiceSelection = () => {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState('one-way');

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Taxi and Transportation Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Advait Tour and Travels",
      "telephone": "+91-79986-66660"
    },
    "areaServed": {
      "@type": "State",
      "name": "Punjab, Haryana, Himachal Pradesh"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Transportation Services",
      "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "One Way Taxi",
          "description": "Point to point travel service"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Round Trip Taxi",
          "description": "Complete round trip service"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Local Tours",
          "description": "City exploration and local sightseeing"
        }
      }]

    }
  };

  const services = [
  {
    id: "one-way",
    name: "One Way",
    category: "Point to Point Travel",
    icon: "ArrowRight",
    image: "https://images.unsplash.com/photo-1598013472363-9aada4f93cbb",
    imageAlt: "Modern white sedan car driving on highway with clear blue sky and mountains in background during daytime",
    description: "Perfect for single destination trips with no return journey. Ideal for airport transfers, city-to-city travel, and one-time relocations.",
    capacity: "4 Passengers",
    luggage: "3 Large Bags",
    startingPrice: "2,500",
    priceNote: "Per trip basis",
    isPopular: true
  },
  {
    id: "local",
    name: "Local Tours",
    category: "City Exploration",
    icon: "MapPin",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_153b768ce-1766772326786.png",
    imageAlt: "Comfortable luxury car interior with leather seats parked in urban city setting with modern buildings visible through windows",
    description: "Explore your city with hourly packages. Perfect for shopping, meetings, sightseeing, and local errands with flexible timing.",
    capacity: "4 Passengers",
    luggage: "2 Medium Bags",
    startingPrice: "1,800",
    priceNote: "4 hours package",
    isPopular: false
  },
  {
    id: "round-trip",
    name: "Round Trip",
    category: "Return Journey",
    icon: "Repeat",
    image: "https://images.unsplash.com/photo-1689026780808-b36b72d52aca",
    imageAlt: "Silver sedan car on scenic mountain road with lush green hills and winding path during golden hour sunset",
    description: "Complete round trip service with return journey included. Best for weekend getaways, pilgrimages, and family trips with assured return.",
    capacity: "4 Passengers",
    luggage: "4 Large Bags",
    startingPrice: "4,500",
    priceNote: "Round trip fare",
    isPopular: true
  },
  {
    id: "tempo",
    name: "Tempo Traveller",
    category: "Group Travel",
    icon: "Users",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11fa0872b-1768440186823.png",
    imageAlt: "White tempo traveller van with multiple rows of comfortable seating parked on road with group of travelers standing nearby",
    description: "Spacious vehicle for group travel with comfortable seating. Perfect for family reunions, corporate outings, and large group tours.",
    capacity: "12-17 Passengers",
    luggage: "10+ Large Bags",
    startingPrice: "8,500",
    priceNote: "Per day basis",
    isPopular: false
  },
  {
    id: "urbania",
    name: "Force Urbania",
    category: "Luxury Group Travel",
    icon: "Star",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e968eef3-1767645976455.png",
    imageAlt: "Luxurious white Force Urbania van with premium exterior design and chrome accents parked in front of modern hotel building",
    description: "Premium luxury vehicle with executive seating and modern amenities. Ideal for VIP travel, corporate events, and luxury group tours.",
    capacity: "13 Passengers",
    luggage: "8 Large Bags",
    startingPrice: "12,000",
    priceNote: "Per day basis",
    isPopular: true
  }];


  const serviceFeatures = {
    "one-way": [
    {
      icon: "Shield",
      title: "Verified Drivers",
      description: "All drivers undergo background verification and regular training"
    },
    {
      icon: "Clock",
      title: "On-Time Pickup",
      description: "Guaranteed pickup within 15 minutes of scheduled time"
    },
    {
      icon: "Navigation",
      title: "GPS Tracking",
      description: "Real-time journey tracking for passenger safety"
    },
    {
      icon: "Wifi",
      title: "Free WiFi",
      description: "Stay connected throughout your journey"
    },
    {
      icon: "Snowflake",
      title: "AC Comfort",
      description: "Climate-controlled vehicles for all weather conditions"
    },
    {
      icon: "Phone",
      title: "24/7 Support",
      description: "Round-the-clock customer assistance available"
    }],

    "local": [
    {
      icon: "Clock",
      title: "Flexible Hours",
      description: "Choose from 4, 8, or 12-hour packages as per your needs"
    },
    {
      icon: "MapPin",
      title: "Multiple Stops",
      description: "Make unlimited stops within city limits"
    },
    {
      icon: "Shield",
      title: "Local Expertise",
      description: "Drivers with extensive knowledge of city routes"
    },
    {
      icon: "Wallet",
      title: "No Hidden Charges",
      description: "Transparent pricing with no surprise fees"
    },
    {
      icon: "Snowflake",
      title: "Comfort Assured",
      description: "Well-maintained AC vehicles for city tours"
    },
    {
      icon: "Phone",
      title: "Easy Booking",
      description: "Book instantly via call or WhatsApp"
    }],

    "round-trip": [
    {
      icon: "Calendar",
      title: "Multi-Day Packages",
      description: "Flexible packages from 2 to 7 days available"
    },
    {
      icon: "Home",
      title: "Driver Accommodation",
      description: "Driver stay arrangements included in package"
    },
    {
      icon: "Navigation",
      title: "Route Planning",
      description: "Optimized routes with scenic stops included"
    },
    {
      icon: "Shield",
      title: "Travel Insurance",
      description: "Complimentary travel insurance for peace of mind"
    },
    {
      icon: "Fuel",
      title: "Fuel Included",
      description: "All fuel costs covered in package price"
    },
    {
      icon: "Phone",
      title: "Trip Coordinator",
      description: "Dedicated coordinator for your entire journey"
    }],

    "tempo": [
    {
      icon: "Users",
      title: "Group Seating",
      description: "Comfortable push-back seats for all passengers"
    },
    {
      icon: "Luggage",
      title: "Ample Storage",
      description: "Large luggage compartment for group baggage"
    },
    {
      icon: "Music",
      title: "Entertainment",
      description: "Music system and charging points available"
    },
    {
      icon: "Shield",
      title: "Safety First",
      description: "First aid kit and emergency equipment onboard"
    },
    {
      icon: "Snowflake",
      title: "AC Comfort",
      description: "Powerful AC system for group comfort"
    },
    {
      icon: "Coffee",
      title: "Refreshments",
      description: "Complimentary water bottles for all passengers"
    }],

    "urbania": [
    {
      icon: "Star",
      title: "Luxury Interiors",
      description: "Premium leather seats with executive comfort"
    },
    {
      icon: "Tv",
      title: "Entertainment System",
      description: "LED TV with premium audio system"
    },
    {
      icon: "Wifi",
      title: "High-Speed WiFi",
      description: "Unlimited internet connectivity throughout journey"
    },
    {
      icon: "Coffee",
      title: "Refreshment Bar",
      description: "Mini refrigerator with complimentary beverages"
    },
    {
      icon: "Shield",
      title: "VIP Service",
      description: "Professional chauffeur with hospitality training"
    },
    {
      icon: "Zap",
      title: "Power Outlets",
      description: "Multiple charging points for all devices"
    }]

  };

  const vehicleGalleries = {
    "one-way": [
    {
      url: "https://images.unsplash.com/photo-1726003514379-033ea2ef59f5",
      alt: "Exterior view of white sedan car with sleek design parked on clean road with modern city buildings in background"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1124086bf-1765856237239.png",
      alt: "Interior dashboard view of sedan showing modern steering wheel, digital display, and comfortable driver seat"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_163f65203-1765164412972.png",
      alt: "Spacious rear passenger seats with leather upholstery and ample legroom in sedan car interior"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_17fb1585b-1767719022818.png",
      alt: "Open car trunk showing large luggage storage capacity with multiple suitcases neatly arranged"
    }],

    "local": [
    {
      url: "https://images.unsplash.com/photo-1652453822986-aba9e3677515",
      alt: "Comfortable sedan car interior with premium leather seats and modern dashboard for city tours"
    },
    {
      url: "https://images.unsplash.com/photo-1582057815451-f84112b838e1",
      alt: "Sedan car driving through busy city street with urban buildings and traffic in background"
    },
    {
      url: "https://images.unsplash.com/photo-1696723853590-a0997301a292",
      alt: "Side profile of sedan car parked near shopping mall entrance with people walking nearby"
    },
    {
      url: "https://images.unsplash.com/photo-1586149661999-5f736491c4eb",
      alt: "Driver seat view showing GPS navigation system and comfortable driving position in sedan"
    }],

    "round-trip": [
    {
      url: "https://images.unsplash.com/photo-1600832782030-4e31b02c8a98",
      alt: "Sedan car on scenic mountain highway with beautiful landscape and clear blue sky for round trip journey"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_161edccaf-1767645973909.png",
      alt: "Well-maintained sedan car exterior showing clean body and proper tire condition for long distance travel"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e00da65f-1767645974985.png",
      alt: "Comfortable rear passenger seats with extra legroom and storage space for multi-day round trip"
    },
    {
      url: "https://images.unsplash.com/photo-1681170211065-2e6b86de52a6",
      alt: "Modern car dashboard with digital display showing route map and distance for round trip planning"
    }],

    "tempo": [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_16f41fd6f-1768440186961.png",
      alt: "White tempo traveller van exterior with multiple windows and spacious design for group travel"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_12a006a70-1766848581108.png",
      alt: "Interior view of tempo traveller showing multiple rows of comfortable push-back seats with headrests"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6efb5fd-1765716730341.png",
      alt: "Tempo traveller van driving on highway with group of passengers visible through windows"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_18f762351-1767645976280.png",
      alt: "Large luggage compartment of tempo traveller with multiple suitcases and bags stored securely"
    }],

    "urbania": [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e968eef3-1767645976455.png",
      alt: "Luxurious Force Urbania van exterior with premium white finish and chrome accents in front of hotel"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1661ae2ce-1765191981302.png",
      alt: "Executive interior of Force Urbania showing leather captain seats with armrests and entertainment system"
    },
    {
      url: "https://images.unsplash.com/photo-1560438840-846c868a5cd3",
      alt: "Premium dashboard of Force Urbania with digital controls, climate system, and luxury steering wheel"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1661ae2ce-1765191981302.png",
      alt: "Spacious cabin of Force Urbania with LED lighting, entertainment screens, and comfortable seating arrangement"
    }]

  };

  const testimonials = {
    "one-way": [
    {
      name: "Rajesh Kumar",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee047f3e-1763293623246.png",
      avatarAlt: "Professional headshot of Indian man with short black hair wearing blue shirt smiling at camera",
      rating: 5,
      comment: "Excellent service from Amritsar to Delhi. Driver was punctual and very professional. The car was clean and comfortable. Highly recommended for one-way trips!",
      route: "Amritsar → Delhi",
      date: "Dec 2025"
    },
    {
      name: "Priya Sharma",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
      avatarAlt: "Professional headshot of Indian woman with long black hair wearing traditional red kurta smiling warmly",
      rating: 5,
      comment: "Used their service for airport transfer. The driver tracked my flight and was waiting when I arrived. Very smooth experience with Advait Tour and Travels.",
      route: "Chandigarh → Delhi Airport",
      date: "Jan 2026"
    },
    {
      name: "Amit Singh",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f39d6cda-1763295757879.png",
      avatarAlt: "Professional headshot of Sikh man with turban and beard wearing white kurta with warm smile",
      rating: 4,
      comment: "Good service and reasonable pricing. The car was well-maintained and driver knew all the shortcuts. Will definitely use again for my business trips.",
      route: "Ludhiana → Shimla",
      date: "Dec 2025"
    }],

    "local": [
    {
      name: "Neha Gupta",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_101904fc7-1763294024485.png",
      avatarAlt: "Professional headshot of young Indian woman with shoulder-length hair wearing blue salwar suit smiling brightly",
      rating: 5,
      comment: "Booked 8-hour package for shopping in Chandigarh. Driver was patient and helped with all stops. Great value for money!",
      route: "Chandigarh Local",
      date: "Jan 2026"
    },
    {
      name: "Vikram Malhotra",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a04865b7-1763296635893.png",
      avatarAlt: "Professional headshot of middle-aged Indian man with grey hair wearing formal black suit and tie",
      rating: 5,
      comment: "Used for business meetings across the city. Very professional service with comfortable car. Driver was well-dressed and courteous.",
      route: "Delhi Local",
      date: "Dec 2025"
    }],

    "round-trip": [
    {
      name: "Harpreet Kaur",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
      avatarAlt: "Professional headshot of Sikh woman wearing colorful dupatta with warm smile and traditional jewelry",
      rating: 5,
      comment: "Perfect for our family trip to Manali. Driver was experienced with hill routes and very helpful. The round trip package was economical and hassle-free.",
      route: "Amritsar ↔ Manali",
      date: "Dec 2025"
    },
    {
      name: "Suresh Patel",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_146e38675-1763293473707.png",
      avatarAlt: "Professional headshot of elderly Indian man with white hair and mustache wearing traditional cream kurta",
      rating: 5,
      comment: "Excellent service for our pilgrimage to Haridwar. Driver was respectful and knew all the temple locations. Highly recommend for religious trips.",
      route: "Delhi ↔ Haridwar",
      date: "Jan 2026"
    }],

    "tempo": [
    {
      name: "Rohit Verma",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffb8dc87-1763296355766.png",
      avatarAlt: "Professional headshot of young Indian man with short hair wearing casual blue t-shirt with friendly smile",
      rating: 5,
      comment: "Booked tempo traveller for our college reunion trip. Spacious and comfortable for 14 people. Driver was friendly and the vehicle was in excellent condition.",
      route: "Chandigarh → Dharamshala",
      date: "Dec 2025"
    },
    {
      name: "Kavita Reddy",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1dab13bb9-1763295757646.png",
      avatarAlt: "Professional headshot of South Indian woman with long braided hair wearing green saree with gold border",
      rating: 4,
      comment: "Great for family gatherings. We were 12 people and everyone was comfortable. Good music system and AC worked perfectly throughout the journey.",
      route: "Delhi → Agra",
      date: "Jan 2026"
    }],

    "urbania": [
    {
      name: "Aditya Chopra",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17479fa05-1763295028727.png",
      avatarAlt: "Professional headshot of businessman in expensive grey suit with styled hair and confident expression",
      rating: 5,
      comment: "Luxury experience for our corporate team outing. The Urbania was like a mobile office with WiFi and charging points. Professional chauffeur and premium service.",
      route: "Delhi → Jaipur",
      date: "Jan 2026"
    },
    {
      name: "Meera Kapoor",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2f2949b-1763295849320.png",
      avatarAlt: "Professional headshot of elegant Indian woman with styled hair wearing designer maroon outfit with pearl jewelry",
      rating: 5,
      comment: "Booked for wedding guests. Everyone was impressed with the luxury interiors and comfort. Worth every penny for special occasions!",
      route: "Amritsar → Chandigarh",
      date: "Dec 2025"
    }]

  };

  const safetyBadges = [
  {
    icon: "ShieldCheck",
    title: "ISO 9001:2015 Certified",
    description: "Quality management system certified"
  },
  {
    icon: "Award",
    title: "Best Travel Service 2025",
    description: "Awarded by North India Tourism Board"
  },
  {
    icon: "Users",
    title: "50,000+ Happy Customers",
    description: "Trusted by thousands across North India"
  },
  {
    icon: "Star",
    title: "4.8/5 Average Rating",
    description: "Based on 12,000+ verified reviews"
  }];


  const popularRoutes = {
    "one-way": [
    { from: "Amritsar", to: "Delhi", price: "4,500", duration: "7 hours", distance: "450 km", bookings: "2,340" },
    { from: "Chandigarh", to: "Shimla", price: "3,200", duration: "4 hours", distance: "115 km", bookings: "1,890" },
    { from: "Ludhiana", to: "Manali", price: "5,800", duration: "8 hours", distance: "310 km", bookings: "1,560" },
    { from: "Jalandhar", to: "Dharamshala", price: "2,800", duration: "3.5 hours", distance: "140 km", bookings: "1,230" }],

    "local": [
    { from: "Chandigarh", to: "City Tour", price: "1,800", duration: "4 hours", distance: "40 km", bookings: "3,450" },
    { from: "Amritsar", to: "Local", price: "2,000", duration: "4 hours", distance: "35 km", bookings: "2,890" },
    { from: "Delhi", to: "Shopping", price: "2,200", duration: "8 hours", distance: "60 km", bookings: "2,340" },
    { from: "Ludhiana", to: "Business", price: "1,600", duration: "4 hours", distance: "30 km", bookings: "1,670" }],

    "round-trip": [
    { from: "Delhi", to: "Haridwar", price: "8,500", duration: "2 days", distance: "440 km", bookings: "1,890" },
    { from: "Amritsar", to: "Manali", price: "12,000", duration: "3 days", distance: "620 km", bookings: "1,450" },
    { from: "Chandigarh", to: "Shimla", price: "6,500", duration: "2 days", distance: "230 km", bookings: "2,120" },
    { from: "Ludhiana", to: "Dharamshala", price: "7,800", duration: "2 days", distance: "280 km", bookings: "980" }],

    "tempo": [
    { from: "Chandigarh", to: "Manali", price: "15,000", duration: "2 days", distance: "310 km", bookings: "890" },
    { from: "Delhi", to: "Agra", price: "12,000", duration: "1 day", distance: "440 km", bookings: "1,230" },
    { from: "Amritsar", to: "Dalhousie", price: "13,500", duration: "2 days", distance: "200 km", bookings: "670" },
    { from: "Ludhiana", to: "Shimla", price: "11,000", duration: "2 days", distance: "240 km", bookings: "560" }],

    "urbania": [
    { from: "Delhi", to: "Jaipur", price: "18,000", duration: "1 day", distance: "550 km", bookings: "450" },
    { from: "Chandigarh", to: "Amritsar", price: "16,000", duration: "1 day", distance: "230 km", bookings: "380" },
    { from: "Amritsar", to: "Manali", price: "22,000", duration: "2 days", distance: "310 km", bookings: "290" },
    { from: "Delhi", to: "Haridwar", price: "15,000", duration: "1 day", distance: "220 km", bookings: "340" }]

  };

  const handleBookNow = (service) => {
    navigate('/booking-confirmation', { state: { service: service?.name } });
  };

  const handleWhatsAppInquiry = (service) => {
    const message = `Hi, I'm interested in ${service?.name} service. Can you provide more details?`;
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleBookRoute = (route) => {
    navigate('/booking-confirmation', { state: { route: `${route?.from} to ${route?.to}` } });
  };

  const currentService = services?.find((s) => s?.id === activeService);

  return (
    <>
      <SEO
        title="Our Services - Advait Tour and Travels | Taxi, Tempo Traveller & Force Urbania Booking"
        description="Explore comprehensive travel services from Advait Tour and Travels: One Way trips, Round trips, Local tours, Tempo Traveller, and luxury Force Urbania. Premium taxi services across North India."
        keywords="taxi services, one way taxi, round trip cab, local tours, tempo traveller booking, Force Urbania rental, outstation taxi, travel services Chandigarh"
        structuredData={structuredData}
        ogTitle="Our Services - Advait Tour and Travels | Taxi, Tempo Traveller & Force Urbania Booking"
        ogDescription="Explore comprehensive travel services from Advait Tour and Travels: One Way trips, Round trips, Local tours, Tempo Traveller, and luxury Force Urbania. Premium taxi services across North India."
        ogUrl="https://www.advaittourandtravels.com/services"
        canonicalUrl="https://www.advaittourandtravels.com/services" />

      <Helmet>
        <title>Our Services - Advait Tour and Travels | Premium Taxi & Travel Solutions</title>
        <meta name="description" content="Explore our comprehensive range of travel services including One Way trips, Local tours, Round trips, Tempo Traveller, and luxury Force Urbania. Trusted travel companion across North India." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16 lg:pt-20">
          <section className="gradient-hero py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <div className="inline-flex items-center gap-2 bg-accent/20 text-accent-foreground px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  <Icon name="Star" size={16} />
                  <span>Premium Travel Services</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
                  Choose Your Perfect Journey
                </h1>
                <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
                  From quick city tours to luxury group travel, we offer comprehensive transportation solutions tailored to your needs
                </p>
              </div>

              <ServiceTabs
                services={services}
                activeService={activeService}
                onServiceChange={setActiveService} />

            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {services?.map((service, index) => (
                  <div
                    key={service?.id}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover-lift border-2 border-transparent hover:border-blue-400 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => {
                      if (service?.id === 'one-way') navigate('/route-selection');
                      else if (service?.id === 'local') navigate('/local-cab');
                      else if (service?.id === 'round-trip') navigate('/route-selection');
                      else if (service?.id === 'tempo') navigate('/tempo-traveller');
                      else if (service?.id === 'urbania') navigate('/force-urbania');
                    }}
                  >
                    <div className="relative h-64 overflow-hidden image-zoom">
                      <img
                        src={service?.image}
                        alt={service?.imageAlt}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                      {service?.isPopular && (
                        <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                          Popular
                        </div>
                      )}
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-blue-200 text-sm mb-1 transition-transform duration-300 group-hover:translate-x-2">{service?.category}</p>
                        <h3 className="text-2xl font-bold text-white mb-1 transition-transform duration-300 group-hover:translate-x-2">
                          {service?.name}
                        </h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {service?.description}
                      </p>
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-gray-600">
                          <p className="font-semibold">{service?.capacity}</p>
                          <p>{service?.luggage}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">₹{service?.startingPrice}</p>
                          <p className="text-xs text-gray-500">{service?.priceNote}</p>
                        </div>
                      </div>
                      <button className="w-full py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all duration-300 btn-hover transform hover:scale-105">
                        Book Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                  Safety & Trust Certifications
                </h2>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
                  Your safety is our priority. We maintain the highest standards of service quality and safety protocols
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {safetyBadges?.map((badge, index) =>
                <SafetyBadge key={index} badge={badge} />
                )}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                  Explore All Our Services
                </h2>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
                  From quick city tours to luxury group travel, find the perfect service for your journey
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {services?.map((service) =>
                <ServiceCard
                  key={service?.id}
                  service={service}
                  onBookNow={handleBookNow}
                  onWhatsAppInquiry={handleWhatsAppInquiry} />

                )}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16 bg-gradient-journey">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-base md:text-lg text-white/90 mb-6 md:mb-8">
                Book now or contact us for custom travel solutions tailored to your needs
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/booking-confirmation')}
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg transition-all duration-200 flex items-center justify-center gap-2 button-hover">

                  <Icon name="Calendar" size={20} />
                  Book Your Journey
                </button>
                <button
                  onClick={() => window.open('https://wa.me/917998666660', '_blank')}
                  className="w-full sm:w-auto bg-white hover:bg-white/90 text-primary px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg transition-all duration-200 flex items-center justify-center gap-2 whatsapp-pulse">

                  <Icon name="MessageCircle" size={20} />
                  WhatsApp Us
                </button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>);

};

export default ServiceSelection;